import React, { useState, useEffect, useRef, useContext } from 'react';
import './styles/BulkUpload.css';
import axios from 'axios';
import { Textarea } from 'baseui/textarea';
import { Input } from 'baseui/input';
import { Select, SIZE } from 'baseui/select';
import { Button, KIND } from 'baseui/button';
import AuthContext from '../shared/AuthContext';
import {
  aiFormDefaults,
  BULK_JSON_PLACEHOLDER,
  formatBulkQuestionsJson,
  normalizeBulkQuestions,
} from '../../utils/bulkQuestionFormat';
import { Block } from 'baseui/block';
import { Notification } from 'baseui/notification';
import { FaUpload, FaEdit, FaMagic } from 'react-icons/fa';
import { SnackbarProvider, useSnackbar, PLACEMENT } from 'baseui/snackbar';

const SUB_CATEGORY1_OPTIONS = [
  { id: 'basics', label: 'Basics' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

const BulkUpload = () => {
  const { apiClient } = useContext(AuthContext);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [formData, setFormData] = useState({ question: '', extraInfo: '' });
  const [aiForm, setAiForm] = useState({
    category: '',
    topic: '',
    subCategory1: '',
    subCategory2: '',
    count: '5',
  });
  const [beautifiedJson, setBeautifiedJson] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [snackNotice, setSnackNotice] = useState({ id: 0, text: '' });

  const pushSnack = (text) => {
    if (!text) return;
    setSnackNotice((prev) => ({ id: prev.id + 1, text }));
  };
  const [placement] = useState(PLACEMENT.bottomRight);
  const [loading, setIsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState([]);
  const [selectedSubCategory1, setSelectedSubCategory1] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/api/get-filters');
        if (cancelled) return;
        const categories = res.data?.category || [];
        const topics = res.data?.topic || [];
        setCategoryOptions(
          categories.map((name) => ({
            id: String(name),
            label: String(name),
          }))
        );
        setTopicOptions(
          topics.map((name) => ({
            id: String(name),
            label: String(name),
          }))
        );
      } catch {
        if (!cancelled) {
          pushSnack(
            'Could not load categories or topics. You can still create new values.'
          );
        }
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const handleSubCategory1Change = ({ value }) => {
    setSelectedSubCategory1(value || []);
    const picked = value?.[0];
    const level = picked?.id ?? picked?.label ?? '';
    setAiForm((prev) => ({
      ...prev,
      subCategory1: level ? String(level).toLowerCase() : '',
    }));
  };

  const handleCategoryChange = ({ value }) => {
    setSelectedCategory(value || []);
    const picked = value?.[0];
    const categoryStr = picked?.label ?? picked?.id ?? '';
    setAiForm((prev) => ({ ...prev, category: String(categoryStr) }));

    if (picked?.isCreatable && categoryStr) {
      setCategoryOptions((opts) => {
        if (opts.some((o) => o.id === categoryStr)) return opts;
        return [...opts, { id: categoryStr, label: categoryStr }];
      });
    }
  };

  const handleTopicChange = ({ value }) => {
    setSelectedTopic(value || []);
    const picked = value?.[0];
    const topicStr = picked?.label ?? picked?.id ?? '';
    setAiForm((prev) => ({ ...prev, topic: String(topicStr) }));

    if (picked?.isCreatable && topicStr) {
      setTopicOptions((opts) => {
        if (opts.some((o) => o.id === topicStr)) return opts;
        return [...opts, { id: topicStr, label: topicStr }];
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleAiFormChange = (e) => {
    const { name, value } = e.target;
    setAiForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyBulkFormat = (parsedJson, defaults) => {
    const { items, errors } = normalizeBulkQuestions(parsedJson, defaults);
    if (errors.length > 0) {
      return { formatted: null, errors, items };
    }
    return { formatted: formatBulkQuestionsJson(items), errors: [], items };
  };

  const extractQuestionsJson = (raw, defaults) => {
    if (raw == null) return { formatted: '', errors: [] };

    let data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch {
        const text = stripMarkdownFences(raw);
        try {
          data = JSON.parse(text);
        } catch {
          const match = text.match(/\[[\s\S]*\]/);
          if (match) {
            try {
              data = JSON.parse(match[0]);
            } catch {
              return { formatted: text, errors: ['Could not parse AI response as JSON.'] };
            }
          } else {
            return { formatted: text, errors: ['Could not parse AI response as JSON.'] };
          }
        }
      }
    }

    const geminiText = data?.candidates?.[0]?.content?.parts?.[0]?.text;
    if (typeof geminiText === 'string') {
      try {
        data = JSON.parse(stripMarkdownFences(geminiText));
      } catch {
        const match = stripMarkdownFences(geminiText).match(/\[[\s\S]*\]/);
        if (match) {
          try {
            data = JSON.parse(match[0]);
          } catch {
            return { formatted: geminiText, errors: ['Could not parse AI response as JSON.'] };
          }
        } else {
          return { formatted: geminiText, errors: ['Could not parse AI response as JSON.'] };
        }
      }
    }

    if (Array.isArray(data)) {
      return applyBulkFormat(data, defaults);
    }
    if (typeof data === 'object' && data !== null && data.question) {
      return applyBulkFormat([data], defaults);
    }

    return { formatted: '', errors: ['AI response was not a question array.'] };
  };

  const stripMarkdownFences = (text) =>
    text
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/\s*```$/i, '')
      .trim();

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);

    try {
      const params = {
        count: Number(aiForm.count) || 5,
      };
      if (aiForm.category.trim()) params.category = aiForm.category.trim();
      if (aiForm.topic.trim()) params.topic = aiForm.topic.trim();
      if (aiForm.subCategory1.trim()) params.subCategory1 = aiForm.subCategory1.trim();
      if (aiForm.subCategory2.trim()) params.subCategory2 = aiForm.subCategory2.trim();

      const response = await axios.get(`${apiUrl}/ai/generate`, {
        params,
        withCredentials: true,
        responseType: 'text',
        transformResponse: [(data) => data],
      });

      const defaults = aiFormDefaults(aiForm);
      const { formatted, errors } = extractQuestionsJson(response.data, defaults);
      if (errors.length > 0 || !formatted) {
        pushSnack(errors[0] || 'Generated JSON did not match the required format.');
        if (formatted) {
          setFormData((prev) => ({ ...prev, extraInfo: formatted }));
        }
        return;
      }
      setFormData((prev) => ({
        ...prev,
        extraInfo: formatted,
      }));
      setBeautifiedJson('');
      pushSnack('Questions generated in bulk format. Review JSON, then Submit to save.');
    } catch (error) {
      console.error('Generate error:', error);
      pushSnack(
        `Generate failed: ${error.response?.data?.message || error.response?.data || 'Failed to generate questions.'}`
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitInFlightRef.current) {
      return;
    }

    let questionsArray;
    try {
      const parsed = JSON.parse(formData.extraInfo);
      const defaults = aiFormDefaults(aiForm);
      const { items, errors } = normalizeBulkQuestions(parsed, defaults);
      if (errors.length > 0) {
        pushSnack(errors.slice(0, 3).join(' '));
        return;
      }
      if (!items.length) {
        pushSnack('Add at least one question to the JSON array.');
        return;
      }
      questionsArray = items;
    } catch {
      pushSnack('Invalid JSON. Fix the payload before submitting.');
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);

    try {
      const response = await axios.post(
        `${apiUrl}/api/add-questions-bulk`,
        questionsArray,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );

      if (response.status === 200 && response.data !== false) {
        const count = questionsArray.length;
        const noun = count === 1 ? 'question' : 'questions';
        pushSnack(`Saved — ${count} ${noun} added to the bank.`);
      } else {
        pushSnack(
          typeof response.data === 'string'
            ? response.data
            : 'Upload failed. The server did not confirm the save.'
        );
      }
    } catch (error) {
      console.error('Error:', error);
      pushSnack(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Upload failed. Please try again.'
      );
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const beautifyJson = () => {
    try {
      const parsed = JSON.parse(formData.extraInfo);
      const { items, errors } = normalizeBulkQuestions(parsed, aiFormDefaults(aiForm));
      if (errors.length > 0) {
        pushSnack(errors.slice(0, 2).join(' '));
        return;
      }
      const formattedJson = formatBulkQuestionsJson(items);
      setBeautifiedJson(formattedJson);
      setFormData((prev) => ({ ...prev, extraInfo: formattedJson }));
    } catch {
      setBeautifiedJson('Invalid JSON format.');
      pushSnack('Invalid JSON — fix syntax before beautifying.');
    }
  };

  const fieldOverrides = {
    Root: {
      style: {
        marginBottom: '4px',
      },
    },
  };

  return (
    <div className="bulk-upload-page">
      <SnackbarProvider
        placement={placement}
        maxWidth="200px"
        overrides={{
          Body: {
            style: {
              fontSize: '12px',
              padding: '8px',
            },
          },
        }}
      >
        <BulkUploadSnackbar notice={snackNotice} />
        <div className="form-container">
          <div className="bulk-layout">
          {/* AI Generate form */}
          <form className="ai-generate-form" onSubmit={handleGenerate}>
            <h3 className="ai-generate-title">Generate with AI</h3>
            <p className="ai-generate-hint">
              Set category, topic, level (subCategory1), and subCategory2. AI keeps
              answers short on the card; longer detail goes in extraInfo automatically.
            </p>

            <div className="ai-generate-grid">
              <div className="ai-field">
                <label htmlFor="category">Category</label>
                <Select
                  id="category"
                  creatable
                  clearable
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  size={SIZE.compact}
                  isLoading={filtersLoading}
                  placeholder={
                    filtersLoading
                      ? 'Loading categories…'
                      : 'Select or create a category'
                  }
                  overrides={{
                    Root: { style: { width: '100%' } },
                    ControlContainer: fieldOverrides.Root,
                  }}
                />
              </div>

              <div className="ai-field">
                <label htmlFor="topic">Topic</label>
                <Select
                  id="topic"
                  creatable
                  clearable
                  options={topicOptions}
                  value={selectedTopic}
                  onChange={handleTopicChange}
                  size={SIZE.compact}
                  isLoading={filtersLoading}
                  placeholder={
                    filtersLoading ? 'Loading topics…' : 'Select or create a topic'
                  }
                  overrides={{
                    Root: { style: { width: '100%' } },
                    ControlContainer: fieldOverrides.Root,
                  }}
                />
              </div>

              <div className="ai-field">
                <label htmlFor="subCategory1">Sub Category 1 (level)</label>
                <Select
                  id="subCategory1"
                  clearable
                  options={SUB_CATEGORY1_OPTIONS}
                  value={selectedSubCategory1}
                  onChange={handleSubCategory1Change}
                  size={SIZE.compact}
                  placeholder="Basics, intermediate, or advanced"
                  overrides={{
                    Root: { style: { width: '100%' } },
                    ControlContainer: fieldOverrides.Root,
                  }}
                />
              </div>

              <div className="ai-field">
                <label htmlFor="subCategory2">Sub Category 2</label>
                <Input
                  id="subCategory2"
                  name="subCategory2"
                  value={aiForm.subCategory2}
                  onChange={handleAiFormChange}
                  placeholder="e.g. collections"
                  size="compact"
                  overrides={fieldOverrides}
                />
              </div>

              <div className="ai-field">
                <label htmlFor="count">Count</label>
                <Input
                  id="count"
                  name="count"
                  type="number"
                  min={1}
                  max={50}
                  value={aiForm.count}
                  onChange={handleAiFormChange}
                  placeholder="5"
                  size="compact"
                  overrides={fieldOverrides}
                />
              </div>
            </div>

            <Button
              type="submit"
              kind={KIND.secondary}
              isLoading={generating}
              disabled={generating}
              overrides={{
                BaseButton: {
                  style: {
                    marginTop: '8px',
                    fontWeight: 'bold',
                  },
                },
              }}
            >
              <FaMagic style={{ marginRight: '8px' }} />
              {generating ? 'Generating…' : 'Generate'}
            </Button>
          </form>

          {/* Bulk upload form */}
          <form className="bulk-upload-form" onSubmit={handleSubmit}>
            <div className="bulk-input-group">
              <h3 className="ai-generate-title">Bulk upload</h3>
              <label>
                <p>JSON array (required fields per item): question, answer, category, topic, subCategory1 (basics | intermediate | advanced), subCategory2, extraInfo</p>
              </label>
              <br />
              <Textarea
                id="extraInfo"
                name="extraInfo"
                value={formData.extraInfo}
                onChange={handleInputChange}
                placeholder={BULK_JSON_PLACEHOLDER}
                size="mini"
                rows={16}
                cols={30}
                clearable
                clearOnEscape
                overrides={{
                  Input: {
                    style: {
                      backgroundColor: '#f9f9f9',
                      borderRadius: '5px',
                      fontSize: '14px',
                      minHeight: '320px',
                    },
                  },
                }}
              />
              <Block
                  className="bulk-upload-actions"
                  display="flex"
                  justifyContent="space-between"
                  marginTop="16px"
                  overrides={{
                    Block: {
                      style: {
                        '@media screen and (max-width: 480px)': {
                          flexDirection: 'column',
                          gap: '12px',
                        },
                      },
                    },
                  }}
              >
                <Button
                  type="submit"
                  kind={KIND.primary}
                  isLoading={submitting}
                  disabled={submitting}
                  overrides={{
                    BaseButton: {
                      style: {
                        width: '48%',
                        color: 'white',
                        fontWeight: 'bold',
                        ':hover': { backgroundColor: '' },
                      },
                    },
                  }}
                >
                  <FaUpload style={{ marginRight: '8px' }} />
                  {submitting ? 'Uploading…' : 'Submit'}
                </Button>
                <Button
                  type="button"
                  onClick={beautifyJson}
                  className="beautify-button"
                  overrides={{
                    BaseButton: {
                      style: {
                        width: '48%',
                        color: 'white',
                        fontWeight: 'bold',
                        ':hover': { backgroundColor: '' },
                      },
                    },
                  }}
                >
                  <FaEdit style={{ marginRight: '8px' }} /> Beautify JSON
                </Button>
              </Block>
            </div>
          </form>
          </div>

          {submissionStatus && (
            <Notification
              kind={
                submissionStatus.startsWith('Success') ? 'positive' : 'negative'
              }
              closeable
              autoHideDuration={5000}
              overrides={{
                Body: {
                  style: {
                    padding: '10px',
                    fontSize: '14px',
                  },
                },
              }}
            >
              {submissionStatus}
            </Notification>
          )}

          {beautifiedJson && (
            <div className="json-output" style={{ marginTop: '30px' }}>
              <div className="json-content">
                <h3>Beautified JSON:</h3>
                <pre
                  style={{
                    backgroundColor: '#f4f4f4',
                    padding: '20px',
                    borderRadius: '5px',
                    fontSize: '14px',
                  }}
                >
                  {beautifiedJson}
                </pre>
              </div>
            </div>
          )}
        </div>
      </SnackbarProvider>
    </div>
  );
};

const BulkUploadSnackbar = ({ notice }) => {
  const { enqueue } = useSnackbar();
  useEffect(() => {
    if (!notice?.text) return;
    enqueue({ message: notice.text });
  }, [notice?.id, notice?.text, enqueue]);

  return null;
};

export default BulkUpload;
